import { forwardRef, useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useApi } from '@/lib/legacy';
import { notifyError, notifySuccess } from '@/lib/notify';
import { currencyFormatter as currencyFormatterJs } from '../../QuotePage/content/QuoteGrids/utils';
import { formatAddress } from '../../../utils/getFormattedAddress';
import { apiHelpers } from '../../../utils';
import { uniqueId } from '../../../ui/utils';
import { usePolicyPageContext as usePolicyPageContextJs } from '../context/PolicyPageContext';
import { ActionButton, ActionRow, Grid, GridGroup, type Row } from '../tabParts';
import { TemporaryLocationsFees } from './TemporaryLocationsFees';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: QuoteGrids/utils.js formats an AG Grid value
const currencyFormatter = currencyFormatterJs as any as (args: { field: unknown; withDecimals?: boolean }) => string;
type PolicyPageState = { endorsements?: Row[]; setTemporaryFees: (fees: unknown) => void; setEndorsements: (endorsements: Row[]) => void };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: pages/PolicyPage/context/PolicyPageContext.jsx
const usePolicyPageContext = usePolicyPageContextJs as any as () => PolicyPageState;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: AG Grid cell params
type CellParams = any;

const FILL_HANDLE_CELL_SELECTION = { handle: { mode: 'fill' } };
const TEMPORARY_LOCATIONS = 'temporaryLocations';

type Group = { id: string; endorsements: Row; locations: Row[]; fees: Row[]; ordinal?: unknown; notes?: string };

const toIsoDate = (value: unknown) => {
  let dateValue: Date;
  if (typeof value === 'string') {
    dateValue = new Date(value);
  } else if (value instanceof Date) {
    dateValue = value;
  } else {
    return null;
  }
  if (isNaN(dateValue.getTime())) return null;
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const day = String(dateValue.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const visibleRow = (row: Row) =>
  Object.entries(row).some(([key, value]) => {
    if (key === 'id') return false;
    return value !== '' && value !== null && value !== undefined && !(typeof value === 'number' && value === 0);
  });

const requestFor = (group: Group) => ({
  endorsementType: TEMPORARY_LOCATIONS,
  endorsementData: {
    premium: group.endorsements?.premium || 0,
    effectiveAt: group.endorsements?.effectiveAt || null,
    effectiveUntil: group.endorsements?.effectiveUntil || null,
    fees: group.fees || [],
  },
  ordinal: group.ordinal,
  rows: (group.locations || []).filter(visibleRow).map((row) => ({
    locationNumber: row.locationNumber,
    locationName: row.locationName,
    locationAddress: row.locationAddress,
    coverages: row.coverages,
  })),
});

/** Temporary locations, in endorsement groups: each with its dates and premium, locations and fees. */
export const TemporaryLocations = forwardRef<unknown, { policy: Row; setGridReference: (ref: unknown) => void; gridReference?: { current?: Row } }>(
  ({ policy, setGridReference, gridReference }) => {
    const [isLoading, setIsLoading] = useState(false);
    const { insertMultipleEndorsements, downloadEndorsement, getEndorsements } = useApi();
    const [feeGridRef, setFeeGridRef] = useState<unknown>();
    const [endorsementGroups, setEndorsementGroups] = useState<Group[]>([]);
    const [downloadingTableId, setDownloadingTableId] = useState<string | null>(null);
    const [deletedRows, setDeletedRows] = useState<Record<string, Row[]>>({});
    const { endorsements = [], setTemporaryFees, setEndorsements } = usePolicyPageContext();

    const transformGroupData = (data: Row | Row[]): Group[] => {
      const updatedData = Array.isArray(data) ? data : [data];
      return updatedData?.map(({ endorsementData, rows, ordinal }) => ({
        id: uniqueId(),
        endorsements: {
          premium: endorsementData?.premium !== undefined && endorsementData?.premium !== null ? endorsementData.premium : policy.netPremium,
          effectiveAt: endorsementData?.effectiveAt,
          effectiveUntil: endorsementData?.effectiveUntil,
        },
        locations: rows || [],
        fees: endorsementData.fees || policy?.fees || [],
        ordinal,
      }));
    };

    const addNewEndorsementGroup = () => {
      setEndorsementGroups((prev) => {
        let lastEndorsement: Group | null | undefined = prev.length ? prev[prev.length - 1] : null;
        if (!lastEndorsement) {
          const temporaryLocations = endorsements?.find(({ endorsementType }) => endorsementType === TEMPORARY_LOCATIONS);
          const formattedData = temporaryLocations ? transformGroupData(temporaryLocations) : [];
          lastEndorsement = formattedData.length ? formattedData[0] : null;
        }
        const premium = lastEndorsement ? lastEndorsement.endorsements?.premium : policy?.netPremium;
        const lastLocations = Array.isArray(lastEndorsement?.locations) ? (lastEndorsement as Group).locations : [];
        const lastLocationNumber = lastLocations.length
          ? Math.max(...lastLocations.map((loc) => parseInt(loc.locationNumber?.replace('T', '') || 0, 10)))
          : 0;
        const newLocations = lastLocations.map((loc, index) => ({ ...loc, locationNumber: `T${lastLocationNumber + index + 1}` }));
        const newEndorsement: Group = { ...(lastEndorsement as Group), id: uniqueId(), endorsements: { premium }, locations: newLocations };
        delete newEndorsement.ordinal;
        return [...prev, { ...newEndorsement }];
      });
    };

    useEffect(() => {
      const temporaryLocations = endorsements?.filter(({ endorsementType }) => endorsementType === TEMPORARY_LOCATIONS);
      setEndorsementGroups(temporaryLocations ? transformGroupData(temporaryLocations) : []);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endorsements]);

    const handleFeeUpdate = (updatedFees: unknown) => {
      setTemporaryFees(updatedFees);
    };

    const getDefaultCoverages = (forPolicy: Row) => {
      const allCoverages = [
        { coverageType: 'comprehensive', coverageId: 6 },
        { coverageType: 'flood', coverageId: 21 },
        { coverageType: 'earthquake', coverageId: 22 },
        { coverageType: 'hail', coverageId: 9 },
        { coverageType: 'collision', coverageId: 7 },
        { coverageType: 'terrorism', coverageId: 11 },
      ];
      const previousCoverages = forPolicy.locations.length > 0 ? forPolicy.locations[0].coverages : [];
      return allCoverages.map((coverage) => {
        const previousCoverage = previousCoverages.find((prev: Row) => prev.coverageId === coverage.coverageId);
        return { ...coverage, payoutLimit: 100000, deductible: previousCoverage?.deductible || 0, aggregate: previousCoverage?.aggregate || 0 };
      });
    };

    const getColumnsDef = (id: string) => {
      const endorsementIndex = endorsementGroups.findIndex((e) => e.id === id);
      if (endorsementIndex === -1) return undefined;
      const selectedEndorsement = { ...(endorsementGroups[endorsementIndex] as Group) };

      const getLocationIndex = (locationNumber: string) =>
        selectedEndorsement.locations.findIndex((location) => location.locationNumber === locationNumber);
      const getColValues = (params: CellParams, coverageType: string, key: string) => {
        const { data } = params;
        const coverage = data.coverages && data.coverages.length && data.coverages.find((each: Row) => each.coverageType === coverageType);
        return coverage ? coverage[key] : '';
      };

      const setGridValues = (params: CellParams) => {
        const { data, newValue } = params;
        const locationIndex = getLocationIndex(params.data.locationNumber);
        const coverageId = params.column.userProvidedColDef.coverageId;
        const editedField = params.colDef.field;
        const coverageIndex = data.coverages.findIndex((coverage: Row) => coverage.coverageId === coverageId);
        const formattedValue = isNaN(Number(newValue)) ? 0 : Number(newValue);

        const updatedRowData = [...selectedEndorsement.locations];
        if (locationIndex !== -1) {
          updatedRowData[locationIndex] = {
            ...updatedRowData[locationIndex],
            coverages: updatedRowData[locationIndex]?.coverages.map((coverage: Row, idx: number) =>
              idx === coverageIndex ? { ...coverage, [editedField]: formattedValue } : coverage
            ),
          };
        }
        if (
          editedField === 'payoutLimit' &&
          updatedRowData[locationIndex]?.coverages.some((covg: Row) => covg.coverageType === 'comprehensive' && covg.coverageId === coverageId)
        ) {
          updatedRowData[locationIndex] = {
            ...updatedRowData[locationIndex],
            coverages: updatedRowData[locationIndex]?.coverages.map((coverage: Row) =>
              coverage.coverageType !== 'comprehensive' ? { ...coverage, payoutLimit: formattedValue } : coverage
            ),
          };
        }
        setEndorsementGroups(endorsementGroups.map((t) => (t.id === id ? { ...t, locations: updatedRowData } : t)));
        return true;
      };

      const createCoverageColumns = ({ coverageType, coverageId }: { coverageType: string; coverageId: number }) => {
        const shortName = apiHelpers.getShortCoveragesName(coverageType);
        return [
          {
            headerName: `${shortName} Per Car Deductible`,
            field: 'deductible',
            dataType: 'number',
            valueGetter: (params: CellParams) => getColValues(params, coverageType, 'deductible'),
            valueFormatter: (field: unknown) => currencyFormatter({ field, withDecimals: true }),
            editable: true,
            valueSetter: (params: CellParams) => setGridValues(params),
            coverageId,
            minWidth: 200,
          },
          {
            headerName: `${shortName} Agg Deductible`,
            field: 'aggregate',
            dataType: 'number',
            valueGetter: (params: CellParams) => getColValues(params, coverageType, 'aggregate'),
            valueFormatter: (field: { value: number }) => (field.value === 0 || isNaN(field.value) ? 'N/A' : currencyFormatter({ field })),
            editable: true,
            valueSetter: (params: CellParams) => setGridValues(params),
            coverageId,
            minWidth: 200,
          },
          {
            headerName: `${shortName} Payout Limit`,
            field: 'payoutLimit',
            dataType: 'number',
            valueFormatter: (field: unknown) => currencyFormatter({ field, withDecimals: true }),
            valueGetter: (params: CellParams) => getColValues(params, coverageType, 'payoutLimit'),
            editable: true,
            valueSetter: (params: CellParams) => setGridValues(params),
            coverageId,
            minWidth: 200,
          },
        ];
      };

      return [
        {
          headerName: 'Location',
          field: 'locationNumber',
          dataType: 'number',
          pinned: true,
          maxWidth: 110,
          editable: true,
          headerCheckboxSelection: true,
          checkboxSelection: true,
        },
        { headerName: 'Name', field: 'locationName', dataType: 'string', pinned: true, editable: true },
        {
          headerName: 'Address',
          field: 'locationAddress',
          dataType: 'string',
          pinned: true,
          minWidth: 250,
          editable: true,
          valueGetter: (params: CellParams) => {
            const visibleAddress = formatAddress(params.data.visibleAddress) || '';
            const locationAddress = formatAddress(params.data.locationAddress) || '';
            if (visibleAddress === locationAddress) {
              return visibleAddress || locationAddress;
            }
            return visibleAddress && locationAddress ? `${visibleAddress}, ${locationAddress}` : visibleAddress || locationAddress;
          },
        },
        ...getDefaultCoverages(policy).map(createCoverageColumns).flat(),
      ];
    };

    const handleAddRow = (endorsementGroupId: string) => {
      setEndorsementGroups((prevEndorsements) => {
        const endorsementIndex = prevEndorsements.findIndex((e) => e.id === endorsementGroupId);
        if (endorsementIndex === -1) return prevEndorsements;
        const updatedEndorsement = { ...(prevEndorsements[endorsementIndex] as Group) };
        const maxLocationNumber = Math.max(...updatedEndorsement.locations?.map((row) => parseInt(row.locationNumber.replace('T', ''))), 0);
        const newRow = { locationNumber: `T${maxLocationNumber + 1}`, coverages: getDefaultCoverages(policy) };
        updatedEndorsement.locations = [...updatedEndorsement.locations, newRow];
        gridReference?.current?.api?.applyTransaction({ add: [newRow] });
        const updatedEndorsements = [...prevEndorsements];
        updatedEndorsements[endorsementIndex] = updatedEndorsement;
        return updatedEndorsements;
      });
    };

    const updateEndorsementField = (endorsementId: string, key: string, value: unknown) => {
      setEndorsementGroups((prevEndorsements) =>
        prevEndorsements.map((endorsement) =>
          endorsement.id === endorsementId ? { ...endorsement, endorsements: { ...endorsement.endorsements, [key]: value } } : endorsement
        )
      );
    };

    const dateColumn = (id: string, field: 'effectiveAt' | 'effectiveUntil', headerName: string) => ({
      headerName,
      ...(field === 'effectiveUntil' ? { headerClass: 'grid-cell-centered' } : {}),
      field,
      dataType: 'string',
      editable: true,
      cellEditorSelector: () => ({ component: 'agDateCellEditor', params: { dateFormat: 'YYYY-MM-DD' } }),
      valueSetter: (params: CellParams) => {
        if (typeof params.newValue !== 'string' && !(params.newValue instanceof Date)) {
          return false;
        }
        const iso = toIsoDate(params.newValue);
        if (iso) {
          params.data[field] = iso;
          updateEndorsementField(id, field, iso);
        }
        return undefined;
      },
      cellDataType: 'string',
    });

    const headerColumns = (id: string) => [
      dateColumn(id, 'effectiveAt', 'Effective At'),
      dateColumn(id, 'effectiveUntil', 'Effective Until'),
      {
        headerName: 'Premium',
        field: 'premium',
        editable: true,
        cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center' },
        valueSetter: (params: CellParams) => {
          updateEndorsementField(id, 'premium', params.newValue);
        },
        valueFormatter: (field: unknown) => currencyFormatter({ field, withDecimals: true }),
      },
    ];

    const handleOnSave = async () => {
      setIsLoading(true);
      try {
        const payload = { endorsements: endorsementGroups.map(requestFor) };
        const response = await insertMultipleEndorsements(policy.id, payload, TEMPORARY_LOCATIONS);
        if (response) {
          setEndorsements(await getEndorsements(policy.id));
        }
        notifySuccess('Saved Successfully.');
      } catch (error) {
        console.error('Error saving endorsements:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const handleOnDownload = async (endorsementGroup: Group) => {
      if (!endorsementGroup || !endorsementGroup.id) {
        console.error('Invalid endorsement group selected.');
        return;
      }
      setDownloadingTableId(endorsementGroup.id);
      try {
        const response = await downloadEndorsement(policy.id, requestFor(endorsementGroup));
        if (response?.data?.value) {
          window.open(response.data.value, '_blank');
        }
      } catch (error) {
        console.error('Error downloading endorsement:', error);
      } finally {
        setDownloadingTableId(null);
      }
    };

    const handleCellValueChanged = useCallback((params: CellParams, endorsementGroupId: string) => {
      const { data, colDef, newValue } = params;
      setEndorsementGroups((prevEndorsements) =>
        prevEndorsements.map((endorsement) => {
          if (endorsement.id !== endorsementGroupId) return endorsement;
          return {
            ...endorsement,
            locations: endorsement.locations.map((row) => (row.locationNumber === data.locationNumber ? { ...row, [colDef.field]: newValue } : row)),
          };
        })
      );
    }, []);

    const handleDeleteRows = (tableId: string) => {
      const rowsToDelete = deletedRows[tableId] || [];
      if (!rowsToDelete.length) {
        notifyError('Please select rows to delete.');
        return;
      }
      setEndorsementGroups((prevTables) =>
        prevTables.map((table) =>
          table.id === tableId
            ? {
                ...table,
                locations: table.locations.filter((row) => !rowsToDelete.some((deletedRow) => deletedRow.locationName === row.locationName)),
              }
            : table
        )
      );
      setDeletedRows((prevState) => {
        const newState = { ...prevState };
        delete newState[tableId];
        return newState;
      });
    };

    const onSelectionChanged = useCallback((context: CellParams, id: string) => {
      if (context) {
        const selectedRows = context.api.getSelectedRows();
        setDeletedRows((prevState) => ({ ...prevState, [id]: selectedRows }));
      }
    }, []);

    return (
      <div>
        {endorsementGroups.map((group, index) => (
          <GridGroup key={group.id}>
            <Input
              type="text"
              defaultValue={`Temp Locations_${index + 1}`}
              value={group?.notes}
              onChange={(e) => setEndorsementGroups(endorsementGroups.map((t) => (t.id === group.id ? { ...t, notes: e.target.value } : t)))}
              placeholder="Add a note..."
              aria-label="Group note"
              className="mb-2.5 h-auto bg-white p-2.5 text-sm font-bold"
            />
            <div className="mb-5 h-[60px] w-full">
              <Grid
                data={Array.isArray(group.endorsements) ? group.endorsements : [group.endorsements]}
                columnDefs={headerColumns(group.id)}
                defaultColDef={{ flex: 1, resizable: true }}
                floatingFilter={false}
                shouldShowPagination={false}
                setGridReference={setGridReference}
              />
            </div>
            <div className="grid-container max-h-[600px] min-h-[200px] overflow-y-auto">
              <Grid
                data={group.locations}
                columns={getColumnsDef(group.id)}
                domLayout="autoHeight"
                rowSelection="multiple"
                floatingFilter={false}
                shouldShowPagination={false}
                cellSelection={FILL_HANDLE_CELL_SELECTION}
                setGridReference={setGridReference}
                onCellValueChanged={(params: CellParams) => handleCellValueChanged(params, group.id)}
                onSelectionChanged={(context: CellParams) => onSelectionChanged(context, group.id)}
              />
            </div>
            <ActionRow className="gap-1.5">
              <ActionButton size="sm" onClick={() => handleAddRow(group.id)}>
                Add Location
              </ActionButton>
              <ActionButton size="sm" loading={downloadingTableId === group.id} onClick={() => handleOnDownload(group)}>
                Download
              </ActionButton>
              <ActionButton size="sm" tone="danger" onClick={() => handleDeleteRows(group.id)}>
                Delete Location(s)
              </ActionButton>
              <ActionButton size="sm" tone="danger" onClick={() => setEndorsementGroups(endorsementGroups.filter((t) => t.id !== group.id))}>
                Delete Table
              </ActionButton>
            </ActionRow>
            <div>
              <h4 className="m-2.5 text-lg font-medium">Fees</h4>
              <TemporaryLocationsFees
                policy={{ ...policy, fees: group.fees }}
                setFeeGridRef={setFeeGridRef}
                onFeeUpdate={(updatedFees: unknown) => handleFeeUpdate(updatedFees)}
                feeGridRef={feeGridRef}
                onSelectionChanged={onSelectionChanged}
                endorsementData={group.endorsements}
                setGridReference={setGridReference}
              />
            </div>
          </GridGroup>
        ))}
        <ActionRow>
          <Button className="m-4 h-10 w-[250px] min-w-[120px] px-4" onClick={addNewEndorsementGroup}>
            Add New Endorsement Group
          </Button>
          <ActionButton onClick={handleOnSave} loading={isLoading}>
            Save
          </ActionButton>
        </ActionRow>
      </div>
    );
  }
);
TemporaryLocations.displayName = 'TemporaryLocations';
